import { reportWSClientDiagnostic } from '@/api/websocket'

const CLIENT_SESSION_STORAGE_KEY = 'agent_store_ws_diagnostic_session_v1'
const PENDING_REPORTS_STORAGE_KEY = 'agent_store_ws_diagnostic_queue_v1'
const MAX_PENDING_REPORTS = 50
const MAX_TIMELINE_EVENTS = 32
const MAX_ERROR_SAMPLES = 10
const CHANNEL_TIMELINE_DEDUP_WINDOW_MS = 1000

const REPORTABLE_EVENTS = new Set([
  'ws_transport_error',
  'ws_disconnected',
  'ws_reconnect_failed_20s',
  'ws_subscription_failed_20s',
  'chat_waiting_no_run_started_10s',
  'chat_waiting_no_run_started_20s',
  'chat_waiting_no_run_started_30s',
  'chat_run_started_no_message_10s',
  'chat_run_started_no_message_20s',
  'chat_run_started_no_message_30s',
  'websocket_reconnect_popup_shown',
  'websocket_reconnect_popup_refresh_clicked',
  'chat_send_failed'
])

const CONNECTION_ERROR_EVENTS = new Set([
  'ws_transport_error',
  'ws_disconnected',
  'ws_reconnect_failed_20s',
  'ws_subscription_failed_20s'
])

const ERROR_SAMPLE_EVENTS = new Set([
  'ws_transport_error',
  'ws_disconnected',
  'chat_channel_error',
  'ws_subscription_failed_20s',
  'chat_send_failed'
])

const CHANNEL_TIMELINE_DEDUP_EVENTS = new Set([
  'chat_channel_subscribing',
  'chat_channel_subscribed'
])

function createRuntimeState() {
  return {
    event_counts: {},
    event_timeline: [],
    first_event_type: '',
    first_event_at: '',
    last_event_type: '',
    last_event_at: '',
    ws: {},
    subscription: {},
    error_samples: [],
    error_sample_overflow_signatures: [],
    error_sample_overflow_count: 0,
    connection_incident_id: '',
    connection_incident_closed: false,
    connection_incident_had_error: false
  }
}

const runtimeState = createRuntimeState()

let flushPromise = null
let sequence = 0

function createId(prefix) {
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return `${prefix}-${crypto.randomUUID()}`
    }
  } catch (error) {
    // Fall back to a timestamp-based identifier below.
  }

  return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`
}

function readStorage(key, fallback) {
  try {
    const value = window.sessionStorage.getItem(key)
    return value ? JSON.parse(value) : fallback
  } catch (error) {
    return fallback
  }
}

function writeStorage(key, value) {
  try {
    window.sessionStorage.setItem(key, JSON.stringify(value))
  } catch (error) {
    // Diagnostic reporting must never affect the chat UI.
  }
}

function getClientSessionId() {
  try {
    const existing = window.sessionStorage.getItem(CLIENT_SESSION_STORAGE_KEY)
    if (existing) return existing
    const created = createId('ws-session')
    window.sessionStorage.setItem(CLIENT_SESSION_STORAGE_KEY, created)
    return created
  } catch (error) {
    return createId('ws-session')
  }
}

function safeText(value, maxLength = 300) {
  if (value === undefined || value === null) return ''
  return String(value)
    .replace(/Bearer\s+[^\s]+/ig, 'Bearer [REDACTED]')
    .replace(/eyJ[A-Za-z0-9_.-]{20,}/g, '[REDACTED]')
    .slice(0, maxLength)
}

function getBrowserContext() {
  if (typeof window === 'undefined') return {}

  const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection
  return {
    online: typeof navigator.onLine === 'boolean' ? navigator.onLine : null,
    visibility_state: document.visibilityState || '',
    user_agent: safeText(navigator.userAgent, 500),
    network_effective_type: connection?.effectiveType || '',
    network_rtt: Number.isFinite(connection?.rtt) ? connection.rtt : null,
    network_downlink: Number.isFinite(connection?.downlink) ? connection.downlink : null
  }
}

function normalizePayload(payload = {}) {
  const normalized = { ...payload }
  if (normalized.error && typeof normalized.error === 'object') {
    normalized.error = {
      code: normalized.error.code ?? null,
      message: safeText(normalized.error.message),
      reason: safeText(normalized.error.reason),
      type: safeText(normalized.error.type, 100)
    }
  }
  if (normalized.raw && typeof normalized.raw === 'object') {
    normalized.raw = {
      ...normalized.raw,
      message: safeText(normalized.raw.message),
      reason: safeText(normalized.raw.reason)
    }
  }
  if (normalized.event_timeline && Array.isArray(normalized.event_timeline)) {
    normalized.event_timeline = normalized.event_timeline.slice(-MAX_TIMELINE_EVENTS).map(item => ({
      type: safeText(item?.type, 100),
      at: safeText(item?.at, 60)
    }))
  }
  return normalized
}

function resetConnectionRuntimeState() {
  const next = createRuntimeState()
  Object.keys(runtimeState).forEach(key => {
    delete runtimeState[key]
  })
  Object.assign(runtimeState, next)
}

function ensureConnectionIncident() {
  if (!runtimeState.connection_incident_id || runtimeState.connection_incident_closed) {
    resetConnectionRuntimeState()
    runtimeState.connection_incident_id = createId('ws-incident')
    runtimeState.connection_incident_closed = false
  }
  return runtimeState.connection_incident_id
}

function updateConnectionLifecycle(event, payload = {}) {
  if (event === 'ws_connecting') {
    if (!runtimeState.connection_incident_id || runtimeState.connection_incident_closed) {
      resetConnectionRuntimeState()
      runtimeState.connection_incident_id = createId('ws-incident')
      runtimeState.connection_incident_closed = false
    }
  }

  if (CONNECTION_ERROR_EVENTS.has(event) &&
      !(event === 'ws_disconnected' && payload.cause === 'client_intentional_close')) {
    ensureConnectionIncident()
    runtimeState.connection_incident_had_error = true
  }

  return {
    incidentId: runtimeState.connection_incident_id,
    isRecovery: event === 'ws_connected' &&
      Boolean(runtimeState.connection_incident_id && runtimeState.connection_incident_had_error)
  }
}

function getPayloadIncidentId(payload = {}) {
  return safeText(
    payload.incident_id || payload.popup?.incident_id || '',
    120
  )
}

function getTimelineIdentity(payload = {}) {
  return safeText(
    payload.conversation_uuid || payload.subscription?.channel || '',
    160
  )
}

function getErrorSampleValue(payload, keys = []) {
  for (const key of keys) {
    const value = payload.raw?.[key] ?? payload.error?.[key] ?? payload[key]
    if (value !== undefined && value !== null && value !== '') return value
  }
  return ''
}

function buildErrorSample(event, payload, occurredAt) {
  if (!ERROR_SAMPLE_EVENTS.has(event)) return null
  if (event === 'ws_disconnected' && payload.cause === 'client_intentional_close') return null

  const eventType = safeText(event, 100)
  const cause = safeText(payload.cause || '', 100)
  const code = getErrorSampleValue(payload, [
    'error_code',
    'disconnected_code',
    'code'
  ])
  const message = safeText(getErrorSampleValue(payload, ['message']), 300)
  const reason = safeText(getErrorSampleValue(payload, ['reason', 'expected_reason']), 300)
  const errorType = safeText(getErrorSampleValue(payload, ['type']), 100)
  const channel = safeText(payload.subscription?.channel || '', 200)
  const signature = [eventType, cause, code, message, reason, errorType, channel]
    .map(value => safeText(value, 300))
    .join('|')
  const occurredAtText = new Date(occurredAt).toISOString()

  return {
    signature,
    event: eventType,
    cause,
    code: code === '' ? null : code,
    message,
    reason,
    type: errorType,
    channel,
    first_at: occurredAtText,
    last_at: occurredAtText,
    count: 1,
    connection_attempt: payload.ws?.connection_attempt ?? null,
    client_state: safeText(payload.ws?.client_state || '', 100),
    channel_state: safeText(payload.subscription?.state || payload.ws?.channel_state || '', 100)
  }
}

function updateErrorSamples(event, payload, occurredAt) {
  const sample = buildErrorSample(event, payload, occurredAt)
  if (!sample) return

  const existing = runtimeState.error_samples.find(item => item.signature === sample.signature)
  if (existing) {
    existing.count = Number(existing.count || 0) + 1
    existing.last_at = sample.last_at
    existing.connection_attempt = sample.connection_attempt
    existing.client_state = sample.client_state
    existing.channel_state = sample.channel_state
    return
  }

  if (runtimeState.error_samples.length >= MAX_ERROR_SAMPLES) {
    if (!runtimeState.error_sample_overflow_signatures.includes(sample.signature)) {
      runtimeState.error_sample_overflow_signatures.push(sample.signature)
      runtimeState.error_sample_overflow_count += 1
    }
    return
  }

  runtimeState.error_samples.push(sample)
}

function updateRuntimeState(event, payload, occurredAt) {
  updateErrorSamples(event, payload, occurredAt)
  const eventType = safeText(event, 100)
  const occurredAtText = new Date(occurredAt).toISOString()
  const identity = getTimelineIdentity(payload)
  const lastTimelineEvent = runtimeState.event_timeline[runtimeState.event_timeline.length - 1]
  const isDuplicateChannelEvent = CHANNEL_TIMELINE_DEDUP_EVENTS.has(eventType) &&
    lastTimelineEvent &&
    lastTimelineEvent.type === eventType &&
    lastTimelineEvent.identity === identity &&
    occurredAt - Number(lastTimelineEvent.client_time_ms || 0) <= CHANNEL_TIMELINE_DEDUP_WINDOW_MS

  if (!isDuplicateChannelEvent) {
    runtimeState.event_counts[eventType] = Number(runtimeState.event_counts[eventType] || 0) + 1
    runtimeState.event_timeline = runtimeState.event_timeline.slice(-(MAX_TIMELINE_EVENTS - 1))
    runtimeState.event_timeline.push({
      type: eventType,
      at: occurredAtText,
      identity,
      client_time_ms: occurredAt
    })
    if (!runtimeState.first_event_type) {
      runtimeState.first_event_type = eventType
      runtimeState.first_event_at = occurredAtText
    }
    runtimeState.last_event_type = eventType
    runtimeState.last_event_at = occurredAtText
  }

  if (payload.ws && typeof payload.ws === 'object') {
    runtimeState.ws = { ...runtimeState.ws, ...payload.ws }
  }
  if (payload.subscription && typeof payload.subscription === 'object') {
    runtimeState.subscription = { ...runtimeState.subscription, ...payload.subscription }
  }
}

function getRuntimeContext() {
  return {
    incident_id: runtimeState.connection_incident_id,
    event_counts: { ...runtimeState.event_counts },
    event_timeline: runtimeState.event_timeline.slice(-MAX_TIMELINE_EVENTS).map(item => ({
      type: item.type,
      at: item.at,
      identity: item.identity
    })),
    first_event_type: runtimeState.first_event_type,
    first_event_at: runtimeState.first_event_at,
    last_event_type: runtimeState.last_event_type,
    last_event_at: runtimeState.last_event_at,
    ws: { ...runtimeState.ws },
    subscription: { ...runtimeState.subscription },
    error_samples: runtimeState.error_samples.map(item => ({ ...item })),
    error_sample_overflow_count: runtimeState.error_sample_overflow_count
  }
}

function shouldReportEvent(event, payload, lifecycle) {
  if (event === 'ws_disconnected' && payload.cause === 'client_intentional_close') {
    return false
  }
  return REPORTABLE_EVENTS.has(event) || lifecycle.isRecovery
}

function buildReport(event, payload = {}, reportSequence = 0, reportId = '') {
  const now = Date.now()
  return {
    report_id: reportId || createId('ws-report'),
    client_session_id: getClientSessionId(),
    sequence: reportSequence,
    source: 'client',
    event: safeText(event, 100),
    client_occurred_at: new Date(now).toISOString(),
    client_time_ms: now,
    route: typeof window !== 'undefined' ? `${window.location.pathname}${window.location.search}`.slice(0, 500) : '',
    app: {
      environment: process.env.NODE_ENV || '',
      build_version: process.env.VUE_APP_VERSION || '',
      ws_host: safeText(process.env.VUE_APP_WS_HOST, 300)
    },
    browser: getBrowserContext(),
    ...normalizePayload(payload)
  }
}

function readPendingReports() {
  const value = readStorage(PENDING_REPORTS_STORAGE_KEY, [])
  if (!Array.isArray(value)) return []
  const reportable = value.filter(item => REPORTABLE_EVENTS.has(item?.event) || Boolean(item?.incident_id))
  if (reportable.length !== value.length) {
    savePendingReports(reportable)
  }
  return reportable
}

function savePendingReports(reports) {
  writeStorage(PENDING_REPORTS_STORAGE_KEY, reports.slice(-MAX_PENDING_REPORTS))
}

function isSameQueuedReportVersion(left, right) {
  return left?.report_id === right?.report_id &&
    Number(left?.sequence ?? -1) === Number(right?.sequence ?? -2) &&
    Number(left?.client_time_ms ?? -1) === Number(right?.client_time_ms ?? -2)
}

async function flushPendingReports() {
  while (true) {
    const pending = readPendingReports()
    if (pending.length === 0) return

    const sendingReport = pending[0]
    try {
      await reportWSClientDiagnostic(sendingReport)

      // 请求期间，同一个 incident 可能已被更新为更完整的快照。
      // 只删除刚刚成功发送的那个版本；如果队列中已有更新版本，则保留并继续发送。
      const latestPending = readPendingReports()
      const sentVersionIndex = latestPending.findIndex(item =>
        isSameQueuedReportVersion(item, sendingReport)
      )
      if (sentVersionIndex >= 0) {
        latestPending.splice(sentVersionIndex, 1)
        savePendingReports(latestPending)
      }
    } catch (error) {
      // 保留最新队列，等待下一次初始化或异常事件触发补传。
      return
    }
  }
}

export function createWebsocketDiagnosticId(prefix = 'ws-incident') {
  return createId(prefix)
}

export function getWebsocketDiagnosticClientSessionId() {
  return getClientSessionId()
}

export function flushWebsocketDiagnostics() {
  if (!flushPromise) {
    flushPromise = flushPendingReports().finally(() => {
      flushPromise = null
    })
  }
  return flushPromise
}

export function recordWebsocketDiagnostic(event, payload = {}) {
  const occurredAt = Date.now()
  const eventSequence = ++sequence
  const normalizedPayload = normalizePayload(payload)
  const lifecycle = updateConnectionLifecycle(event, normalizedPayload)
  const explicitIncidentId = getPayloadIncidentId(normalizedPayload)
  const incidentId = explicitIncidentId || lifecycle.incidentId
  const reportId = incidentId ? `ws-report-${incidentId}` : ''

  updateRuntimeState(event, normalizedPayload, occurredAt)

  // 正常连接、订阅、发送成功和 publication 只在内存中累计，不请求 Laravel。
  if (!shouldReportEvent(event, normalizedPayload, lifecycle)) {
    return {
      reported: false,
      event,
      sequence: eventSequence,
      client_occurred_at: new Date(occurredAt).toISOString(),
      diagnostic_context: getRuntimeContext()
    }
  }

  const report = buildReport(event, {
    ...normalizedPayload,
    incident_id: incidentId,
    diagnostic_context: getRuntimeContext()
  }, eventSequence, reportId)
  const pending = readPendingReports()
  const existingIndex = reportId
    ? pending.findIndex(item => item?.report_id === reportId)
    : -1
  if (existingIndex >= 0) {
    pending.splice(existingIndex, 1, report)
  } else {
    pending.push(report)
  }
  savePendingReports(pending)
  void flushWebsocketDiagnostics()

  if (lifecycle.isRecovery) {
    runtimeState.connection_incident_closed = true
  }

  return report
}

export function appendDiagnosticTimeline(timeline = [], type, at = new Date().toISOString()) {
  const next = Array.isArray(timeline) ? timeline.slice(-MAX_TIMELINE_EVENTS) : []
  next.push({ type: safeText(type, 100), at: safeText(at, 60) })
  return next.slice(-MAX_TIMELINE_EVENTS)
}
